import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { omit } from 'lodash';
import { fetchServiceCatalog } from '../queries/serviceCatalog';
import {
    getServiceCatalogContext,
    getServiceCatalogCacheStateForOrder,
    serviceCatalogKey,
} from '../utilities/serviceCatalog';

const normalizeOrders = (orders) => {
    if (!orders) return [];
    return Array.isArray(orders) ? orders : [orders];
};

const ServiceCatalogContext = createContext(null);

const useServiceCatalogStoreState = () => {
    const [catalogs, setCatalogs] = useState({});
    const [errors, setErrors] = useState({});
    const inFlight = useRef(new Set());

    // Mirror the latest catalogs/errors into refs so ensureCatalogs reads the
    // current cache without listing them as deps. That keeps ensureCatalogs a
    // STABLE identity — otherwise every fetch resolution rebuilds it, rebuilds
    // the memoized store, and re-fires every consumer's ensure effect (churn,
    // and an infinite fetch loop the moment the inFlight/cache guard regresses).
    const catalogsRef = useRef(catalogs);
    const errorsRef = useRef(errors);
    catalogsRef.current = catalogs;
    errorsRef.current = errors;
    // Every context asked for, by key, so refreshCatalogs can re-fetch them.
    const contextsRef = useRef({});

    // Held in a ref so ensureCatalogs stays identity-stable (see above).
    const client = useApolloClient();
    const clientRef = useRef(client);
    clientRef.current = client;

    const ensureCatalogs = useCallback((contexts = []) => {
        contexts.forEach((context) => {
            const key = serviceCatalogKey(context);
            contextsRef.current[key] = context;
            if (catalogsRef.current[key] || errorsRef.current[key] || inFlight.current.has(key)) return;

            inFlight.current.add(key);
            fetchServiceCatalog(clientRef.current, context)
                .then((catalog) => {
                    setCatalogs((prev) => {
                        const next = {
                            ...prev,
                            [key]: { catalog },
                        };
                        catalogsRef.current = next;
                        return next;
                    });
                })
                .catch((error) => {
                    console.error(error);
                    setErrors((prev) => {
                        const next = {
                            ...prev,
                            [key]: error,
                        };
                        errorsRef.current = next;
                        return next;
                    });
                })
                .finally(() => {
                    inFlight.current.delete(key);
                });
        });
    }, []);

    // Re-read a shipper's catalogs from the network after a catalog write. A new row belongs to
    // every scope that contains it (a global row to all of them), so each of the shipper's
    // contexts is refreshed, not just the writer's. The current entry stays until its
    // replacement arrives — validation never drops back to loading — and a failed refresh keeps
    // it rather than flagging services unavailable. Identity-stable, like ensureCatalogs.
    const refreshCatalogs = useCallback((shipperId) => {
        const targets = Object.entries(contextsRef.current).filter(([, context]) => context.shipperId === shipperId);
        return Promise.all(
            targets.map(([key, context]) =>
                fetchServiceCatalog(clientRef.current, context, { fetchPolicy: 'network-only' })
                    .then((catalog) => {
                        setCatalogs((prev) => {
                            const next = { ...prev, [key]: { catalog } };
                            catalogsRef.current = next;
                            return next;
                        });
                        // A context whose first load failed recovers here.
                        if (errorsRef.current[key]) {
                            setErrors((prev) => {
                                const next = omit(prev, key);
                                errorsRef.current = next;
                                return next;
                            });
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                    })
            )
        );
    }, []);

    return useMemo(
        () => ({
            catalogs,
            errors,
            ensureCatalogs,
            refreshCatalogs,
        }),
        [catalogs, errors, ensureCatalogs, refreshCatalogs]
    );
};

export const ServiceCatalogProvider = ({ children }) => {
    const store = useServiceCatalogStoreState();
    return <ServiceCatalogContext.Provider value={store}>{children}</ServiceCatalogContext.Provider>;
};

export const useServiceCatalogs = (orders, fallback = {}) => {
    const sharedStore = useContext(ServiceCatalogContext);
    const localStore = useServiceCatalogStoreState();
    const store = sharedStore || localStore;
    const { ensureCatalogs } = store;

    const contexts = useMemo(() => {
        const byKey = new Map();
        normalizeOrders(orders).forEach((order) => {
            const context = getServiceCatalogContext(order, fallback);
            if (!context) return;
            byKey.set(serviceCatalogKey(context), context);
        });
        return Array.from(byKey.values());
    }, [orders, fallback.shipperId, fallback.shipper_id]);

    // Depend on the stable ensureCatalogs, NOT the whole store: store rebuilds on
    // every catalog/error resolution (consumers need the fresh data), but this
    // effect must fire only when the set of contexts changes — otherwise it
    // re-runs per resolution. ensureCatalogs is identity-stable (reads via refs).
    useEffect(() => {
        ensureCatalogs(contexts);
    }, [contexts, ensureCatalogs]);

    const loading = useMemo(
        () =>
            contexts.some(
                (context) => !store.catalogs[serviceCatalogKey(context)] && !store.errors[serviceCatalogKey(context)]
            ),
        [contexts, store.catalogs, store.errors]
    );

    return { catalogs: store.catalogs, errors: store.errors, loading, refreshCatalogs: store.refreshCatalogs };
};

export const useServiceCatalog = (order, fallback = {}) => {
    const { catalogs, errors, refreshCatalogs } = useServiceCatalogs(order, fallback);
    const state = getServiceCatalogCacheStateForOrder(order, catalogs, errors, fallback);

    return {
        serviceCatalog: state.catalog,
        serviceCatalogError: state.error,
        catalogs,
        errors,
        loading: state.loading,
        refreshCatalogs,
    };
};
